// LoadAccountContextFlow — the ONE "reload the selected account's context" entry point
// (ledger B6, analysis 07 R4). Not a boot, not a switch: the account stays the same and only its
// server-side context is re-read, so `session.status` never moves and nothing is reset.
//
// WHY IT IS ITS OWN CLASS. Contract 11 §18 and contract 22 §21 both spell the seam
// `AccountBootstrapFlow.loadSelectedAccountContext(force)` — the v3 shape, where it was a second
// public method on the boot flow (`Orphex-New-Front/src/features/account/flows/
// AccountBootstrapFlow.ts:76-86`). v4 keeps the METHOD NAME those consumers are written against
// but not the host: contract 07 §8 is "each is one command class / one responsibility", which is
// why the two switch halves are their own classes rather than more `BootstrapV2Flow` methods, and
// `AccountBootstrapFlow` is already 342 of its 400 lines. `useAccount().loadSelectedAccountContext`
// is the surface; this class is what it delegates to.
//
// NORMALISING v3's TWO ENTRY POINTS (ORP-18890 §5). v3 reloaded account context two different
// ways and BE-07-09 records the split:
//   • `loadSelectedAccountContext(true)` — the two granular GETs (`/generic/accessible-accounts/
//     {id}/list-accounts` + `/generic/user_access_info`), used by `?success=1` and
//     `?package_downgraded` (`router/index.js:190`, `:206`) and by every settings mutation.
//     It refreshed `accountDetail` + `userAccessInfo` and NOTHING else — in particular it never
//     re-ran `applyUserPackages`, so a package that changed under it never reached `permissions`
//     or `packageId`.
//   • `execute(true)` — a whole re-boot, used by `?stripe_session` (`router/index.js:207` →
//     `getAccounts()` → `:76`) and by the profile/account/MFA/avatar panes. It re-fetched
//     everything AND recomputed packages, at the cost of re-running the entire boot.
// One seam replaces both: one `bootAccountOnly(refresh)` call — the account-level preset already
// carries `session_access` (user + accounts + access info, so ledger B6's "accounts refetch" is
// the same request), `account_detail`, `rbac`, `account_core.workspaces` and the nav sections —
// followed by `finalize()`. That is the recompute the granular path was missing, and it is the
// only thing the re-boot did that the granular path did not.
//
// It deliberately does NOT do the rest of a re-boot: no workspace-scoped sections
// (dimensions/integrations/settings/feature_flags/compositions), no `session.status`, no
// `degradedSections`, no reset, no logout. The two switch flows draw the same line.
import { BootstrapService } from '../service/BootstrapService'
import type { PackageSignal } from '../service/PackageService'
import { useAccountStore, type AccountStore } from '../stores/accountStore'
import { useWorkspaceStore, type WorkspaceStore } from '../stores/workspaceStore'

import { FlowToolkit, toApiError } from './flowToolkit'
import { dispatchBootstrap } from './hydratorRegistry'

import type { ApiError } from '@/shared/http'

/** The outcome of a context reload. Returned rather than toasted — the flow stays headless and the
 *  copy stays with the caller (contract §13), exactly like the two switch flows. `failed` carries
 *  the CLASSIFIED `ApiError`; v3 swallowed it into a `console.error` (`:83-85`). */
export type AccountContextReloadOutcome =
  { status: 'skipped' } | { status: 'reloaded' } | { status: 'failed'; error: ApiError }

export interface LoadAccountContextHooks {
  /** Pricing / limit / onboarding SIGNAL raised by `finalize` — the same hook the boot and
   *  workspace-switch flows expose. Load-bearing for module 11: a post-Stripe reload is precisely
   *  when the package limit modal should be re-evaluated. */
  onPackageSignal?: (signal: PackageSignal) => void
}

export interface LoadAccountContextFlowOptions {
  bootstrapService?: BootstrapService
  accountStore?: AccountStore
  workspaceStore?: WorkspaceStore
  dispatch?: typeof dispatchBootstrap
  hooks?: LoadAccountContextHooks
}

export class LoadAccountContextFlow {
  private readonly bootstrapService: BootstrapService
  private readonly accountStore: AccountStore
  /** The hydrate + finalize toolkit shared with the boot + switch flows. Its `hooks` is THIS
   *  flow's object by reference, so a late-registered `onPackageSignal` still fires. */
  private readonly toolkit: FlowToolkit

  constructor(options: LoadAccountContextFlowOptions = {}) {
    this.bootstrapService = options.bootstrapService ?? new BootstrapService()
    this.accountStore = options.accountStore ?? useAccountStore()
    this.toolkit = new FlowToolkit({
      accountStore: this.accountStore,
      workspaceStore: options.workspaceStore ?? useWorkspaceStore(),
      dispatch: options.dispatch ?? dispatchBootstrap,
      hooks: options.hooks ?? {},
    })
  }

  /**
   * Reload the selected account's context.
   *   • no account selected → `skipped`, ZERO network (v3 `:77` `!accountStore.account`).
   *   • context already warm and `force=false` → `skipped`, ZERO network (v3 `:77`
   *     `accountDetail && !reload`). This is the COLD-FILL path: it exists so a caller can say
   *     "make sure context is loaded" without paying for a round trip when it already is.
   *   • otherwise → `bootAccountOnly` → hydrate account level → `finalize()`.
   *
   * `force` carries BOTH halves of "reload it for real": it bypasses the warm short-circuit above
   * AND sets `refresh=true` on the request, the server-side cache-bust BE-07-09 requires for the
   * post-Stripe read (the write may still be settling, so a cached account_detail is the exact
   * wrong answer). A cold fill has nothing stale to bust and so does not ask for one.
   */
  public async execute(force = false): Promise<AccountContextReloadOutcome> {
    const accountId = this.accountStore.accountId
    if (accountId === undefined) return { status: 'skipped' }
    if (this.accountStore.accountDetail && !force) return { status: 'skipped' }

    try {
      const result = await this.bootstrapService.bootAccountOnly(Number(accountId), {
        refresh: force,
      })
      this.toolkit.hydrateAccountLevel(result)
    } catch (error) {
      // Classified and RETURNED, never swallowed: v3 logged it to the console and resolved as if
      // the reload had worked (`:83-85`), so a failed post-Stripe reload showed the old package
      // with a success toast over it. Nothing is reset and nobody is logged out.
      return { status: 'failed', error: toApiError(error) }
    }

    // The half v3's granular path never had. `finalize` recomputes permissions + packageId from
    // the freshly hydrated `accountDetail`/`rbac` and raises the package signal — without it a
    // downgrade/upgrade would land in the store and never reach a single gate.
    this.toolkit.finalize()
    return { status: 'reloaded' }
  }
}
